/**
 * Mounted once inside the authenticated app shell (Dashboard). Establishes the
 * analytics identity from Redux + Auth0, emits a session_start, and fires a
 * page_view on every route change so the full user journey is captured with no
 * per-page wiring. Renders nothing.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { identify, trackPageView, track, initAutoCapture } from './analytics';

const AnalyticsTracker = () => {
  const location = useLocation();
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const tenantId = useSelector((s) => s.userInfo.tenantId);
  const email = useSelector((s) => s.userInfo.email);
  const identifiedRef = useRef(false);

  // Start global click auto-capture once (records every meaningful button/link/menu click).
  useEffect(() => { initAutoCapture(); }, []);

  // Establish identity once the userInfo slice is populated, then mark the session.
  useEffect(() => {
    let subId = null;
    try { subId = localStorage.getItem('subId'); } catch { /* ignore */ }
    if (!subId && !email) return;
    identify({ userId: subId, email, tenantId, isAdmin });
    if (!identifiedRef.current) {
      identifiedRef.current = true;
      track('session_start', { entryPath: location.pathname });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, tenantId, isAdmin]);

  // Page view on every path change (and the first render once identified).
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

export default AnalyticsTracker;
